import React, { useEffect, useRef, useState } from 'react'

import AnimationLink from './Link'

export function normalizeNavPath(path) {
  if (!path) return '/'
  const t = path.replace(/\/$/, '')
  return t === '' ? '/' : t
}

export function isServiceNavItemActive(item, path) {
  const norm = normalizeNavPath(path)
  if (normalizeNavPath(item.link) === norm) return true
  return item.items?.some(sub => normalizeNavPath(sub.link) === norm) ?? false
}

function findActiveBranch(items, path) {
  return items.find(sub => sub.items?.length && isServiceNavItemActive(sub, path)) ?? null
}

const BranchChevron = () => (
  <span className="header-services-dropdown__branch-chevron" aria-hidden="true" />
)

export const DesktopServicesDropdown = ({ items, path, hex = '#322EFF' }) => {
  const norm = normalizeNavPath(path)
  const shellRef = useRef(null)
  const clearTimerRef = useRef(null)
  const [activeBranchLink, setActiveBranchLink] = useState(
    () => findActiveBranch(items, path)?.link ?? null
  )

  useEffect(() => {
    const active = findActiveBranch(items, path)
    if (active) {
      setActiveBranchLink(active.link)
    }
  }, [items, path])

  useEffect(
    () => () => {
      if (clearTimerRef.current) {
        clearTimeout(clearTimerRef.current)
      }
    },
    []
  )

  const scheduleBranchClear = () => {
    if (clearTimerRef.current) {
      clearTimeout(clearTimerRef.current)
    }
    clearTimerRef.current = setTimeout(() => {
      if (!shellRef.current?.matches(':hover')) {
        setActiveBranchLink(null)
      }
    }, 120)
  }

  const cancelBranchClear = () => {
    if (clearTimerRef.current) {
      clearTimeout(clearTimerRef.current)
      clearTimerRef.current = null
    }
  }

  const openBranch = link => {
    cancelBranchClear()
    setActiveBranchLink(link)
  }

  const activeBranch = items.find(sub => sub.link === activeBranchLink && sub.items?.length) ?? null

  return (
    <div
      ref={shellRef}
      className={
        'header-services-dropdown__shell' +
        (activeBranch ? ' header-services-dropdown__shell--split' : '')
      }
      onMouseLeave={scheduleBranchClear}
      onMouseEnter={cancelBranchClear}
    >
      <div className="header-services-dropdown__panel">
        <ul className="header-services-dropdown__primary" role="none">
          {items.map(sub => {
            const isCurrent = norm === normalizeNavPath(sub.link)
            const hasFlyout = sub.items?.length > 0
            const isBranchOpen = activeBranchLink === sub.link

            if (!hasFlyout) {
              return (
                <li
                  key={sub.link}
                  className="header-services-dropdown__item"
                  role="none"
                  onMouseEnter={() => openBranch(null)}
                >
                  <AnimationLink
                    paintDrip
                    hex={hex}
                    to={sub.link}
                    className={
                      'header-services-dropdown__link' +
                      (isCurrent ? ' header-services-dropdown__link--current' : '')
                    }
                    aria-current={isCurrent ? 'page' : undefined}
                    role="menuitem"
                  >
                    {sub.name}
                  </AnimationLink>
                </li>
              )
            }

            return (
              <li
                key={sub.link}
                className={
                  'header-services-dropdown__item header-services-dropdown__item--branch' +
                  (isBranchOpen ? ' header-services-dropdown__item--branch-open' : '') +
                  (isServiceNavItemActive(sub, path) ? ' header-services-dropdown__item--branch-active' : '')
                }
                role="none"
                onMouseEnter={() => openBranch(sub.link)}
                onFocus={() => openBranch(sub.link)}
              >
                <AnimationLink
                  paintDrip
                  hex={hex}
                  to={sub.link}
                  className={
                    'header-services-dropdown__link header-services-dropdown__link--branch' +
                    (isCurrent ? ' header-services-dropdown__link--current' : '')
                  }
                  aria-current={isCurrent ? 'page' : undefined}
                  role="menuitem"
                >
                  <span className="header-services-dropdown__link-label">{sub.name}</span>
                  <BranchChevron />
                </AnimationLink>
              </li>
            )
          })}
        </ul>

        <div
          className={
            'header-services-dropdown__secondary' +
            (activeBranch ? ' header-services-dropdown__secondary--visible' : '')
          }
          role="group"
          aria-label={activeBranch?.name}
          aria-hidden={!activeBranch}
          onMouseEnter={() => activeBranch && openBranch(activeBranch.link)}
        >
          {activeBranch ? (
            <ul className="header-services-dropdown__secondary-list" role="none">
              {activeBranch.items.map(nested => {
                const isNestedCurrent = norm === normalizeNavPath(nested.link)
                return (
                  <li key={nested.link} className="header-services-dropdown__item" role="none">
                    <AnimationLink
                      paintDrip
                      hex={hex}
                      to={nested.link}
                      className={
                        'header-services-dropdown__link' +
                        (isNestedCurrent ? ' header-services-dropdown__link--current' : '')
                      }
                      aria-current={isNestedCurrent ? 'page' : undefined}
                      role="menuitem"
                    >
                      {nested.name}
                    </AnimationLink>
                  </li>
                )
              })}
            </ul>
          ) : null}
        </div>
      </div>
    </div>
  )
}
